/**
 * 通知音 (Web Audio API による短いチャイム)。
 *
 * - 音声 asset を追加せず、Web Audio API で 2 音の短いチャイムを合成する。
 * - 自動再生制限への配慮: AudioContext は初期状態で suspended のことがある。
 *   「テスト再生」など user gesture 経由で resume すると以降の自動再生も解放される。
 *   解放されていない / 非対応環境でも例外を出さず無音で終了する (画面を壊さない)。
 * - 通知改善 B (案 D): 再生できない場合は理由を console.warn で 1 理由につき 1 回だけ残す
 *   (PII なし。連続ポーリングで noisy にならないよう同一理由は再出力しない)。
 */

type AudioContextCtor = typeof AudioContext;

let sharedCtx: AudioContext | null = null;

/** 既に warn 済みの理由 (同一理由を 1 セッション 1 回に抑える)。 */
const warnedReasons = new Set<string>();

/**
 * 通知音が再生できない理由を PII なしで console.warn に残す (1 理由 1 回)。
 * 通知内容・ユーザー情報は一切含めない。detail はブラウザ例外オブジェクトのみ。
 */
function warnChimeIssue(reason: string, detail?: unknown): void {
  if (warnedReasons.has(reason)) return;
  warnedReasons.add(reason);
  if (detail !== undefined) {
    console.warn(`[notificationSound] 通知音を再生できません: ${reason}`, detail);
  } else {
    console.warn(`[notificationSound] 通知音を再生できません: ${reason}`);
  }
}

function getAudioContext(): AudioContext | null {
  // 非ブラウザ環境 (テスト等) は対象外。warn も出さない。
  if (typeof window === 'undefined') return null;
  const Ctor: AudioContextCtor | undefined =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: AudioContextCtor }).webkitAudioContext;
  if (!Ctor) {
    warnChimeIssue('AudioContext 非対応ブラウザ');
    return null;
  }
  try {
    if (!sharedCtx) sharedCtx = new Ctor();
    return sharedCtx;
  } catch (err) {
    warnChimeIssue('AudioContext の生成に失敗', err);
    return null;
  }
}

/** 1 音を short envelope (クリックノイズ回避) 付きで鳴らす。 */
function playTone(ctx: AudioContext, freq: number, startAt: number, durationSec: number): void {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(freq, startAt);
  gain.gain.setValueAtTime(0.0001, startAt);
  gain.gain.exponentialRampToValueAtTime(0.18, startAt + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, startAt + durationSec);
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(startAt);
  osc.stop(startAt + durationSec + 0.02);
}

/**
 * 短い通知チャイムを再生する。
 *
 * 自動再生がブロックされている / 非対応の環境でも例外を投げず無音で終了する。
 * @returns 再生を試行できたら true / 再生不能環境なら false (テスト再生ボタンの判定に使用)
 */
export function playNotificationChime(): boolean {
  const ctx = getAudioContext();
  if (!ctx) return false;
  try {
    if (ctx.state === 'suspended') {
      // user gesture 内なら resume で即 running になる。ユーザー操作前の自動再生は
      // ブラウザ制限でブロックされ得る (resume の Promise は保留のままになることがある)。
      void ctx.resume().catch((err) => warnChimeIssue('AudioContext resume が拒否されました', err));
      // 少し待っても suspended のままなら自動再生制限でブロック中と判断して warn (1 回だけ)。
      setTimeout(() => {
        if (sharedCtx && sharedCtx.state === 'suspended') {
          warnChimeIssue(
            'AudioContext が suspended のまま (ユーザー操作前の自動再生制限。設定 > 個人設定 の「通知音をテスト再生」で解除できます)',
          );
        }
      }, 1000);
    }
    const now = ctx.currentTime;
    playTone(ctx, 880, now, 0.12); // A5
    playTone(ctx, 1174.66, now + 0.1, 0.18); // D6
    return true;
  } catch (err) {
    warnChimeIssue('チャイム再生処理で例外', err);
    return false;
  }
}
